'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Cookie, X, Settings } from 'lucide-react';
import { useTranslatedRoute } from '@/hooks';
const COOKIE_CONSENT_NAME = 'cookie-consent';
const COOKIE_PREFERENCES_NAME = 'cookie-preferences';
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}
const CookieConsentBanner = () => {
  const $ = _c(20);
  const t = useTranslations();
  const {
    buildRoute
  } = useTranslatedRoute();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [preferences, setPreferences] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const consent = Cookies.get(COOKIE_CONSENT_NAME);
      if (!consent) {
        setShowBanner(true);
      }
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = prefs => {
      Cookies.set(COOKIE_CONSENT_NAME, "true", {
        expires: 365
      });
      Cookies.set(COOKIE_PREFERENCES_NAME, JSON.stringify(prefs), {
        expires: 365
      });
      setShowBanner(false);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const savePreferences = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const allAccepted = {
        necessary: true,
        analytics: true,
        marketing: true
      };
      savePreferences(allAccepted);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleAcceptAll = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const necessaryOnly = {
        necessary: true,
        analytics: false,
        marketing: false
      };
      savePreferences(necessaryOnly);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleAcceptNecessary = t5;
  let t6;
  if ($[6] !== preferences) {
    t6 = () => {
      savePreferences(preferences);
    };
    $[6] = preferences;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSaveSettings = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setShowBanner(false);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleClose = t7;
  let t8;
  if ($[9] !== buildRoute) {
    t8 = buildRoute("contracts/cookie-policy");
    $[9] = buildRoute;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const cookiePolicyRoute = t8;
  let t9;
  if ($[11] !== cookiePolicyRoute || $[12] !== handleSaveSettings || $[13] !== preferences || $[14] !== showBanner || $[15] !== showSettings || $[16] !== t) {
    t9 = showBanner && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed right-4 bottom-4 left-4 z-50 mx-auto max-w-6xl select-none sm:right-6 sm:bottom-6 sm:left-6"><div className="bg-background/95 supports-backdrop-filter:bg-background/80 rounded-lg border shadow-2xl backdrop-blur"><div className="relative p-6"><button onClick={handleClose} className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none" aria-label={t("close")}><X className="h-4 w-4" /></button>{!showSettings ? <div className="flex flex-col gap-4"><div className="flex items-start gap-3"><Cookie className="text-primary mt-1 h-6 w-6 shrink-0" /><div className="flex-1"><h3 className="text-lg font-semibold">{t("cookie_consent_title")}</h3><p className="text-muted-foreground mt-2 text-sm">{t.rich("cookie_consent_description_with_link", {
                    link: chunks => <Link href={cookiePolicyRoute} className="text-brand font-medium hover:underline">{chunks}</Link>
                  })}</p></div></div><div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end"><Button variant="outline" size="sm" onClick={() => setShowSettings(true)} className="sm:mr-auto"><Settings className="h-4 w-4" />{t("cookie_settings")}</Button><Button variant="outline" size="sm" onClick={handleAcceptNecessary}>{t("cookie_accept_necessary")}</Button><Button size="sm" onClick={handleAcceptAll}>{t("cookie_accept_all")}</Button></div></div> : <div className="flex flex-col gap-4"><div className="flex items-start gap-3"><Cookie className="text-primary mt-1 h-6 w-6 shrink-0" /><div className="flex-1"><h3 className="text-lg font-semibold">{t("cookie_settings")}</h3><p className="text-muted-foreground mt-2 text-sm">{t("cookie_settings_description")}</p></div></div><Separator /><div className="space-y-4"><div className="flex items-center justify-between space-x-4"><div className="flex-1 space-y-1"><Label htmlFor="necessary" className="text-sm font-medium">{t("cookie_necessary_title")}</Label><p className="text-muted-foreground text-xs">{t("cookie_necessary_description")}</p></div><Switch id="necessary" checked={preferences.necessary} disabled={true} className="opacity-50" /></div><Separator /><div className="flex items-center justify-between space-x-4"><div className="flex-1 space-y-1"><Label htmlFor="analytics" className="text-sm font-medium">{t("cookie_analytics_title")}</Label><p className="text-muted-foreground text-xs">{t("cookie_analytics_description")}</p></div><Switch id="analytics" checked={preferences.analytics} onCheckedChange={checked => setPreferences({
                  ...preferences,
                  analytics: checked
                })} /></div><Separator /><div className="flex items-center justify-between space-x-4"><div className="flex-1 space-y-1"><Label htmlFor="marketing" className="text-sm font-medium">{t("cookie_marketing_title")}</Label><p className="text-muted-foreground text-xs">{t("cookie_marketing_description")}</p></div><Switch id="marketing" checked={preferences.marketing} onCheckedChange={checked_0 => setPreferences({
                  ...preferences,
                  marketing: checked_0
                })} /></div></div><div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="outline" size="sm" onClick={() => setShowSettings(false)}>{t("back")}</Button><Button size="sm" onClick={handleSaveSettings}>{t("save_preferences")}</Button></div></div>}</div></div></motion.div>;
    $[11] = cookiePolicyRoute;
    $[12] = handleSaveSettings;
    $[13] = preferences;
    $[14] = showBanner;
    $[15] = showSettings;
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <AnimatePresence>{t9}</AnimatePresence>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
export default CookieConsentBanner;